import { useMemo, useState } from "react";
import { useReadyAuth } from "@/auth";
import { useSignalDetail, useSignalFeed } from "@/features/signals/hooks";
import { CuratorPracticeControl } from "@/features/signals/components/modal/CuratorPracticeControl";
import { PracticeClassificationPanel } from "@/features/signals/components/modal/PracticeClassificationPanel";
import { CuratorPublicationControl } from "./CuratorPublicationControl";

/**
 * Settings-only administration for published signals. The parent Settings
 * landmark is capability-gated, while every consequential read/write keeps its
 * existing server-authorized function boundary. No signal-feed action is
 * exposed from the ordinary Signal detail surface.
 */
export function SignalAdministration(): JSX.Element {
  const { user } = useReadyAuth();
  const feed = useSignalFeed("all", user.id, "all", "most_recent", null);
  const [query, setQuery] = useState("");
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const detail = useSignalDetail(selectedSignalId, user.id);

  const filteredSignals = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return feed.signals;
    return feed.signals.filter(
      (signal) =>
        signal.company.toLowerCase().includes(term) || signal.headline.toLowerCase().includes(term)
    );
  }, [feed.signals, query]);

  const selectedSummary = feed.signals.find((signal) => signal.id === selectedSignalId) ?? null;
  const selectedSignal = detail.data ?? selectedSummary;

  const clearSelection = () => {
    setSelectedSignalId(null);
    feed.refetch();
  };

  return (
    <details className="settings-disclosure signal-administration">
      <summary className="settings-disclosure__summary">
        <span>
          <strong>Published signal administration</strong>
          <small>
            Manage publication, practice assignments, and practice classification in Settings.
          </small>
        </span>
        <span className="settings-disclosure__summary-meta">
          {feed.status === "success" ? `${feed.total} published` : "Loading"}
          <span className="settings-disclosure__chevron" aria-hidden="true">
            ⌄
          </span>
        </span>
      </summary>

      <div className="settings-disclosure__body signal-administration__body">
        <p className="signal-administration__intro">
          Choose a published signal before using these administrator controls. Unpublishing removes
          it from the user feed; practice assignments change curator-managed practice membership.
        </p>

        <div className="signal-administration__picker">
          <label htmlFor="settings-signal-search">Find a published signal</label>
          <input
            id="settings-signal-search"
            type="search"
            value={query}
            placeholder="Search loaded signals by company or title"
            onChange={(event) => setQuery(event.target.value)}
          />
          <label htmlFor="settings-signal-select">Published signal</label>
          <select
            id="settings-signal-select"
            value={selectedSignalId ?? ""}
            disabled={feed.status !== "success" || filteredSignals.length === 0}
            onChange={(event) => setSelectedSignalId(event.target.value || null)}
          >
            <option value="">Select a signal…</option>
            {filteredSignals.map((signal) => (
              <option key={signal.id} value={signal.id}>
                {signal.company} — {signal.headline}
              </option>
            ))}
          </select>
        </div>

        {feed.status === "pending" ? (
          <p className="signal-administration__message" role="status">
            Loading published signals…
          </p>
        ) : null}
        {feed.status === "error" ? (
          <p className="signal-administration__message" role="alert">
            Published signals could not be loaded. Refresh Settings and try again.
          </p>
        ) : null}
        {feed.status === "success" && filteredSignals.length === 0 ? (
          <p className="signal-administration__message">No loaded signal matches that search.</p>
        ) : null}

        {feed.hasNextPage ? (
          <button
            type="button"
            className="sx-btn sx-btn-p3 signal-administration__more"
            disabled={feed.isFetchingNextPage}
            onClick={feed.fetchNextPage}
          >
            {feed.isFetchingNextPage ? "Loading…" : "Load more published signals"}
          </button>
        ) : null}

        {selectedSignalId && detail.isError ? (
          <p className="signal-administration__message" role="alert">
            The selected signal details could not be loaded. Choose it again or refresh Settings.
          </p>
        ) : null}

        {selectedSignal ? (
          <section
            className="signal-administration__selected"
            aria-label="Selected signal controls"
          >
            <header className="signal-administration__selected-head">
              <span>{selectedSignal.company}</span>
              <strong>{selectedSignal.headline}</strong>
            </header>
            <div className="signal-administration__controls">
              <CuratorPublicationControl
                key={`publication:${selectedSignal.id}`}
                signalId={selectedSignal.id}
                onUnpublished={clearSelection}
              />
              <CuratorPracticeControl
                key={`practice:${selectedSignal.id}`}
                signalId={selectedSignal.id}
                classification={selectedSignal.practiceClassification}
              />
              <PracticeClassificationPanel
                classification={selectedSignal.practiceClassification}
                hydrating={detail.isPlaceholderData || detail.isPending}
              />
            </div>
          </section>
        ) : null}
      </div>
    </details>
  );
}
